import { useState, useEffect } from 'react'
import { setUrlParams } from '../../utils/urlState.js'
import { Upload, FileText, Download, Eye, Loader2, Plus, X, MessageSquare, History, Shield, Trash2, Sparkles, ChevronDown, Archive } from 'lucide-react'
import { formatDate } from '../../utils/helpers'
import api from '../../services/api'
import { useAuth } from '../../hooks/useAuth'
import FileViewerModal from '../common/FileViewerModal.jsx'
import DocumentFlowPanel from '../platform/DocumentFlowPanel.jsx'
import DossiersPanel from './DossiersPanel.jsx'
import { commentColors, initialsOf } from '../../utils/docComments.js'
import { useTranslation } from 'react-i18next'

const LEVEL_BADGE = {
  view: 'bg-gray-100 text-gray-600 border-gray-200',
  comment: 'bg-blue-50 text-blue-700 border-blue-200',
  edit: 'bg-green-50 text-green-700 border-green-200',
}
const LEVEL_OPTIONS = ['none', 'view', 'comment', 'edit']


/**
 * DocumentsTab — per-project document co-working space (v2.7).
 * Upload → team views/comments → editors upload new versions (full history) →
 * PM sets per-member permissions. AI summary sheet on top of each document.
 */
export default function DocumentsTab({ project, highlightId, dossierId = null, isArchived = false, onArtifactChange }) {
  const { t: tDoc } = useTranslation('projectsMisc')
  const { t: tc } = useTranslation('common')
  const { user } = useAuth()
  const [docs, setDocs] = useState([])
  const [canManage, setCanManage] = useState(false)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)

  const [expanded, setExpanded] = useState(null)          // doc id
  // Document Flow: is a data assistant connected at all, and which doc's panel is open.
  // Checked once — when nothing is connected the option never appears, so teams unrelated to it
  // see no extra clutter.
  const [assistantOn, setAssistantOn] = useState(false)
  const [flowOpen, setFlowOpen] = useState(null)          // doc id
  useEffect(() => {
    let alive = true
    api.get('/platform/assistant/connection')
      .then(r => { if (alive) setAssistantOn(!!r.data?.connected) })
      .catch(() => { if (alive) setAssistantOn(false) })
    return () => { alive = false }
  }, [])
  const [versions, setVersions] = useState({})            // docId -> [...]
  const [comments, setComments] = useState({})            // docId -> {my_permission, comments}
  const [perms, setPerms] = useState({})                  // docId -> [...members]
  const [newComment, setNewComment] = useState('')
  const [commentBusy, setCommentBusy] = useState(false)
  const [quotedText, setQuotedText] = useState('')   // inline-comment: text the comment anchors to
  const [replyTo, setReplyTo] = useState(null)        // {id, user_name} when replying to a comment
  const [editingComment, setEditingComment] = useState(null)  // comment id currently being edited
  const [editCommentText, setEditCommentText] = useState('')
  const captureSelection = () => {
    const sel = (typeof window !== 'undefined' && window.getSelection) ? String(window.getSelection()) : ''
    const t = sel.trim()
    if (t) setQuotedText(t.length > 500 ? t.slice(0, 500) : t)
  }
  const [reportBusy, setReportBusy] = useState(null)     // docId while generating an AI report
  const [delivFor, setDelivFor] = useState(null)          // doc being submitted as a deliverable
  const [delivMs, setDelivMs] = useState([])              // the consultant's submittable milestones
  const [delivPick, setDelivPick] = useState('')
  const [delivBusy, setDelivBusy] = useState(false)
  const isConsultant = user?.role === 'Consultant'

  const [showUpload, setShowUpload] = useState(false)
  const [upTitle, setUpTitle] = useState('')
  const [upDesc, setUpDesc] = useState('')
  const [upFiles, setUpFiles] = useState([])
  const [upProgress, setUpProgress] = useState(null)   // {done, total} while uploading
  const [uploading, setUploading] = useState(false)

  const [verModal, setVerModal] = useState(null)          // doc object
  const [verFile, setVerFile] = useState(null)
  const [verNote, setVerNote] = useState('')
  const [verBusy, setVerBusy] = useState(false)

  const [viewer, setViewer] = useState(null)              // {url, filename, download, doc?}
  const [viewerComment, setViewerComment] = useState('')  // composer inside the viewer side panel
  const [viewerCommentBusy, setViewerCommentBusy] = useState(false)
  const [activeAnchor, setActiveAnchor] = useState(null)  // comment id whose passage is highlighted/scrolled-to
  const [aiSumBusy, setAiSumBusy] = useState(null)        // doc id whose comment summary is generating
  const [highlighted, setHighlighted] = useState(null)
  // Two features under one roof: the Document Flow for single documents, and Dossiers -- packs of
  // documents assembled, approved and submitted as one.
  // A dossier named in the address (a copied team link) opens straight on the Dossiers view.
  const [view, setView] = useState(dossierId || new URLSearchParams(window.location.search).get('view') === 'dossiers' ? 'dossiers' : 'flow')

  const base = `/projects/${project?.id}/documents`

  const load = async () => {
    if (!project?.id) return
    setLoading(true); setError(null)
    try {
      const r = await api.get(`${base}/`)
      setDocs(r.data?.documents || [])
      setCanManage(!!r.data?.can_manage)
    } catch (e) {
      setError(e.response?.data?.detail || 'Could not load documents.')
    } finally {
      setLoading(false)
    }
  }
  useEffect(() => { load() }, [project?.id])

  // Deep-link highlight (?tab=documents&highlight=<docId>)
  useEffect(() => {
    if (!highlightId || loading) return
    const el = document.getElementById(`doc-${highlightId}`)
    if (!el) return
    el.scrollIntoView({ behavior: 'smooth', block: 'center' })
    setHighlighted(String(highlightId))
    const t = setTimeout(() => setHighlighted(null), 2600)
    return () => clearTimeout(t)
  }, [highlightId, loading, docs])

  const toggleExpand = async (doc) => {
    if (expanded === doc.id) { setExpanded(null); return }
    setExpanded(doc.id)
    // Lazy-load versions + comments (+ permissions if manager)
    try {
      const [v, c] = await Promise.all([
        api.get(`${base}/${doc.id}/versions`),
        api.get(`${base}/${doc.id}/comments`),
      ])
      setVersions(prev => ({ ...prev, [doc.id]: v.data || [] }))
      setComments(prev => ({ ...prev, [doc.id]: c.data || { comments: [] } }))
      if (canManage) {
        const p = await api.get(`${base}/${doc.id}/permissions`)
        setPerms(prev => ({ ...prev, [doc.id]: p.data?.members || [] }))
      }
    } catch (e) {
      setError(e.response?.data?.detail || 'Could not load document details.')
    }
  }

  // Load a document's comments into state if not already there (used by the viewer panel).
  const ensureComments = async (doc) => {
    if (!doc || comments[doc.id]) return
    try {
      const c = await api.get(`${base}/${doc.id}/comments`)
      setComments(prev => ({ ...prev, [doc.id]: c.data || { comments: [] } }))
    } catch { /* panel just shows empty */ }
  }
  useEffect(() => { if (viewer?.doc) ensureComments(viewer.doc) }, [viewer?.doc?.id])

  // Post a comment from the viewer's side panel (Mam #22: comment while reading the sheet).
  const postViewerComment = async () => {
    const doc = viewer?.doc
    const content = viewerComment.trim()
    if (!doc || !content) return
    setViewerCommentBusy(true)
    try {
      await api.post(`${base}/${doc.id}/comments`, {
        content,
        quoted_text: quotedText || null,
        parent_id: replyTo?.id || null,
      })
      setViewerComment(''); setQuotedText(''); setReplyTo(null)
      await reloadComments(doc.id)
    } catch (e) {
      setError(e.response?.data?.detail || 'Could not post comment.')
    } finally {
      setViewerCommentBusy(false)
    }
  }

  // AI summary of the comment thread (Mam #22) — generated on demand, stored on the doc.
  const summarizeComments = async (doc) => {
    setAiSumBusy(doc.id); setError(null)
    try {
      const r = await api.post(`${base}/${doc.id}/comments/summary`)
      setDocs(prev => prev.map(d => d.id === doc.id ? { ...d, ai_comment_summary: r.data.ai_comment_summary } : d))
    } catch (e) {
      setError(e.response?.data?.detail || 'Could not summarize the comments.')
    } finally {
      setAiSumBusy(null)
    }
  }

  // 4.3: AI-generate a report from the document + its comments → saved as a new team document.
  const generateReport = async (doc) => {
    setReportBusy(doc.id); setError(null)
    try {
      const r = await api.post(`${base}/${doc.id}/generate-report`)
      await load()
      alert(`✓ ${r.data.title} created.\n\n${(r.data.preview || '').slice(0, 600)}${(r.data.preview || '').length > 600 ? '…' : ''}`)
    } catch (e) {
      setError(e.response?.data?.detail || 'Could not generate the report.')
    } finally {
      setReportBusy(null)
    }
  }

  // 4.4: submit a document (its latest version) as a deliverable against the consultant's milestone.
  const openSubmitDeliv = async (doc) => {
    setDelivFor(doc); setDelivPick(''); setDelivMs([])
    try {
      const r = await api.get(`/projects/${project.id}/milestones/`)
      const mine = (r.data || []).filter(m => m.consultant_id === user?.id
        && (!m.scope_status || ['Accepted', 'Approved'].includes(m.scope_status)))
      setDelivMs(mine)
    } catch { /* leave empty */ }
  }
  const doSubmitDeliv = async () => {
    if (!delivPick) return
    setDelivBusy(true); setError(null)
    try {
      await api.post(`${base}/${delivFor.id}/submit-as-deliverable`, { milestone_id: parseInt(delivPick) })
      setDelivFor(null)
      alert('Submitted as a deliverable — the PM has been notified to review it.')
    } catch (e) {
      setError(e.response?.data?.detail || 'Could not submit as a deliverable.')
    } finally {
      setDelivBusy(false)
    }
  }

  // Several files at once, one request each. Each becomes its own document with its own
  // summary sheet, comments and versions — that is the unit the team works on. Sequential
  // rather than one big body because the gateway caps a request at ~50 MB, and a batch that
  // fails as a whole tells you nothing about which file was the problem.
  // GENERATE THE PROJECT REPORT.
  //
  // Written from what the project already holds — milestones, deliverables, money and the
  // activity history. The server says in the file, and in a header here, whether a model wrote
  // the prose or the facts were simply laid out, because a reader deciding how far to trust a
  // summary should be told which.
  const [projReportBusy, setProjReportBusy] = useState(false)
  // The report shapes on offer. None is a normal answer and the report then uses its own
  // default headings, so this never blocks generating one.
  const [reportTemplates, setReportTemplates] = useState([])
  const [pickingReport, setPickingReport] = useState(false)
  useEffect(() => {
    api.get('/templates/?kind=report')
      .then(r => setReportTemplates(r.data?.items || []))
      .catch(() => setReportTemplates([]))
  }, [])

  const generateProjectReport = async (templateId = null) => {
    setPickingReport(false)
    setProjReportBusy(true); setError(null)
    try {
      const res = await api.post(
        `/projects/${project.id}/report${templateId ? `?template_id=${templateId}` : ''}`,
        null, { responseType: 'blob' })
      const cd = res.headers?.['content-disposition'] || ''
      const match = /filename="?([^";]+)"?/.exec(cd)
      const url = URL.createObjectURL(res.data)
      const a = document.createElement('a')
      a.href = url
      a.download = match ? match[1] : `project-${project.id}-report.docx`
      document.body.appendChild(a); a.click(); a.remove()
      URL.revokeObjectURL(url)
      setProjReportNote(res.headers?.['x-report-source'] === 'ai'
        ? 'Report downloaded — summarised with AI from the project record.'
        : 'Report downloaded — compiled directly from the project record, without AI.')
    } catch (e) {
      // The failure body is a Blob, because of responseType above; read it back before showing.
      let detail = ''
      try { detail = JSON.parse(await e.response?.data?.text?.() || '{}')?.detail || '' } catch { /* not JSON */ }
      setError(detail || 'The report could not be written. Nothing has been changed.')
    } finally { setProjReportBusy(false) }
  }
  const [projReportNote, setProjReportNote] = useState(null)

  const submitUpload = async (e) => {
    e.preventDefault()
    if (!upFiles.length) return
    if (upFiles.length === 1 && !upTitle.trim()) return
    setUploading(true); setError(null)
    const failed = []
    for (let i = 0; i < upFiles.length; i++) {
      setUpProgress({ done: i, total: upFiles.length })
      const fd = new FormData()
      // A typed title names ONE document. With several files each keeps its own name, because
      // five documents all called the same thing is worse than five filenames.
      fd.append('title', (upFiles.length === 1 && upTitle.trim()) || upFiles[i].name)
      if (upDesc.trim()) fd.append('description', upDesc.trim())
      fd.append('file', upFiles[i])
      try {
        await api.post(`${base}/`, fd, { headers: { 'Content-Type': 'multipart/form-data' } })
      } catch (e2) {
        failed.push(`${upFiles[i].name} — ${e2.response?.data?.detail || 'upload failed'}`)
      }
    }
    setUpProgress(null); setUploading(false)
    setShowUpload(false); setUpTitle(''); setUpDesc(''); setUpFiles([])
    // Whatever did land is kept. Naming the ones that did not beats discarding the lot.
    if (failed.length) setError(`${failed.length} of ${upFiles.length} didn't upload: ${failed.join('; ')}`)
    await load()
  }

  const submitVersion = async (e) => {
    e.preventDefault()
    if (!verFile || !verModal) return
    setVerBusy(true); setError(null)
    try {
      const fd = new FormData()
      fd.append('file', verFile)
      if (verNote.trim()) fd.append('note', verNote.trim())
      await api.post(`${base}/${verModal.id}/versions`, fd, { headers: { 'Content-Type': 'multipart/form-data' } })
      setVerModal(null); setVerFile(null); setVerNote('')
      await load()
      if (expanded) setExpanded(null)
    } catch (e2) {
      setError(e2.response?.data?.detail || 'Version upload failed.')
    } finally {
      setVerBusy(false)
    }
  }

  const postComment = async (doc) => {
    const content = newComment.trim()
    if (!content) return
    setCommentBusy(true)
    try {
      await api.post(`${base}/${doc.id}/comments`, {
        content,
        quoted_text: quotedText || null,
        parent_id: replyTo?.id || null,
      })
      setNewComment(''); setQuotedText(''); setReplyTo(null)
      await reloadComments(doc.id)
    } catch (e) {
      setError(e.response?.data?.detail || 'Could not post comment.')
    } finally {
      setCommentBusy(false)
    }
  }

  // Open a document in the in-app viewer (used by the whole card and the "View" button).
  const openViewer = (doc, versionNo = null) => setViewer({
    url: `${base}/${doc.id}/download${versionNo ? `?version=${versionNo}` : ''}`,
    filename: doc.latest?.original_filename || doc.title,
    download: () => download(doc, versionNo),
    doc,
  })

  // Reload a document's comments AND resync its badge count from the authoritative list, so a
  // cascade-delete of a threaded comment (parent + replies) never leaves the count drifting.
  const reloadComments = async (docId) => {
    const c = await api.get(`${base}/${docId}/comments`)
    setComments(prev => ({ ...prev, [docId]: c.data }))
    const n = (c.data?.comments || []).length
    setDocs(prev => prev.map(d => d.id === docId ? { ...d, comment_count: n } : d))
    return c.data
  }

  const startEditComment = (c) => { setEditingComment(c.id); setEditCommentText(c.content) }
  const cancelEditComment = () => { setEditingComment(null); setEditCommentText('') }

  const saveEditComment = async (doc, c) => {
    const content = editCommentText.trim()
    if (!content) return
    try {
      await api.put(`${base}/${doc.id}/comments/${c.id}`, { content })
      cancelEditComment()
      await reloadComments(doc.id)
    } catch (e) {
      setError(e.response?.data?.detail || 'Could not update the comment.')
    }
  }

  const deleteComment = async (doc, c) => {
    const hasReplies = (comments[doc.id]?.comments || []).some(x => x.parent_id === c.id)
    if (!window.confirm(hasReplies
      ? 'Delete this comment and its replies? This cannot be undone.'
      : 'Delete this comment? This cannot be undone.')) return
    try {
      await api.delete(`${base}/${doc.id}/comments/${c.id}`)
      if (activeAnchor === c.id) setActiveAnchor(null)
      await reloadComments(doc.id)
    } catch (e) {
      setError(e.response?.data?.detail || 'Could not delete the comment.')
    }
  }

  // Can the current user modify this comment? (its author, or a doc manager)
  const canEditComment = (c) => !!user && (c.user_id === user.id || canManage)

  const setLevel = async (doc, userId, level) => {
    try {
      await api.put(`${base}/${doc.id}/permissions`, { user_id: userId, level })
      const p = await api.get(`${base}/${doc.id}/permissions`)
      setPerms(prev => ({ ...prev, [doc.id]: p.data?.members || [] }))
    } catch (e) {
      setError(e.response?.data?.detail || 'Could not update permission.')
    }
  }

  const removeDoc = async (doc) => {
    if (!window.confirm(`Delete "${doc.title}" and all its versions & comments? This cannot be undone.`)) return
    try {
      await api.delete(`${base}/${doc.id}`)
      await load()
    } catch (e) {
      setError(e.response?.data?.detail || 'Could not delete the document.')
    }
  }

  const download = async (doc, versionNo = null) => {
    try {
      const r = await api.get(`${base}/${doc.id}/download`, {
        params: { disposition: 'attachment', ...(versionNo ? { version: versionNo } : {}) },
        responseType: 'blob',
      })
      if (r.headers['content-type']?.includes('application/json')) {
        const json = JSON.parse(await r.data.text())
        if (json?.url) { window.open(json.url, '_blank'); return }
      }
      const url = URL.createObjectURL(r.data)
      const a = document.createElement('a')
      a.href = url
      a.download = doc.latest?.original_filename || doc.title
      a.click()
      setTimeout(() => URL.revokeObjectURL(url), 60_000)
    } catch (e) {
      setError(e.response?.data?.detail || 'Download failed.')
    }
  }

  const canDelete = (doc) => canManage || doc.uploader_id === user?.id

  const archiveDoc = async (doc) => {
    if (!window.confirm(`Archive "${doc.title}"? It starts a 2-year retention clock and locks the document from further edits.`)) return
    try {
      await api.post(`${base}/${doc.id}/archive`)
      await load()
      onArtifactChange?.()
    } catch (e) {
      setError(e.response?.data?.detail || 'Could not archive the document.')
    }
  }

  // Admin only, and only while the project itself is not archived: that state is final.
  const unarchiveDoc = async (doc) => {
    if (!window.confirm(`Un-archive "${doc.title}"? It unlocks for edits and its retention clock is cleared.`)) return
    try {
      await api.post(`${base}/${doc.id}/unarchive`)
      await load()
      onArtifactChange?.()
    } catch (e) {
      setError(e.response?.data?.detail || 'Could not un-archive the document.')
    }
  }

  const viewSwitch = (
    <div className="flex gap-1 border-b border-gray-200">
      {[['flow', 'Document Flow'], ['dossiers', 'Dossiers']].map(([key, label]) => (
        <button key={key} onClick={() => { setView(key); setUrlParams({ view: key, dossier: null }) }}
                className={`px-4 py-2 text-sm font-medium border-b-2 -mb-px ${view === key
                  ? 'border-blue-600 text-blue-700' : 'border-transparent text-gray-500 hover:text-gray-800'}`}>
          {label}
          {key === 'dossiers' && <span className="ml-1.5 text-[10px] uppercase tracking-wide bg-blue-100 text-blue-700 rounded px-1.5 py-0.5">New</span>}
        </button>
      ))}
    </div>
  )

  if (view === 'dossiers') {
    return (
      <div className="space-y-4">
        {viewSwitch}
        <DossiersPanel project={project} isArchived={isArchived} initialDossierId={dossierId ? Number(dossierId) : null} />
      </div>
    )
  }

  if (loading) return <p className="text-sm text-gray-500 py-6 text-center">{tDoc('loading_documents')}</p>

  return (
    <div className="space-y-4">
      {viewSwitch}
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-lg font-semibold text-gray-900">{tDoc('shared_documents')}</h3>
          <p className="text-xs text-gray-500 mt-0.5">
            Upload documents for the team to view, comment on, and edit (versioned).
            {canManage ? ' You control each member’s access per document.' : ''}
          </p>
        </div>
        <div className="flex items-center gap-2 relative">
          {/* THE PROJECT WRITTEN UP. Available on archived projects too: a finished project is
              exactly when somebody asks for its report. */}
          {canManage && (
            <button
              onClick={() => reportTemplates.length
                ? setPickingReport(v => !v)
                : generateProjectReport()}
              disabled={projReportBusy}
              title="Everything recorded on this project, summarised as a document"
              className="flex items-center gap-1.5 px-4 py-2 text-sm font-medium bg-white text-gray-700 border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50"
            >
              {projReportBusy
                ? <><Loader2 className="w-4 h-4 animate-spin" /> Writing…</>
                : <><FileText className="w-4 h-4" /> Generate project report</>}
            </button>
          )}

          {/* Which shape. Shown only when there is more than the default to choose from. */}
          {pickingReport && (
            <div className="absolute right-0 mt-2 z-20 w-72 bg-white border border-gray-200 rounded-xl shadow-lg p-2">
              <p className="px-2 py-1 text-xs text-gray-500">Write it under which headings?</p>
              <button onClick={() => generateProjectReport()}
                      className="w-full text-left rounded-lg px-3 py-2 hover:bg-gray-50 text-sm text-gray-900">
                Standard report
                <span className="block text-xs text-gray-500">Our default headings.</span>
              </button>
              {reportTemplates.map(rt => (
                <button key={rt.id} onClick={() => generateProjectReport(rt.id)}
                        className="w-full text-left rounded-lg px-3 py-2 hover:bg-gray-50 text-sm text-gray-900">
                  {rt.name}
                  {rt.description && <span className="block text-xs text-gray-500">{rt.description}</span>}
                </button>
              ))}
            </div>
          )}
        {!isArchived && (
          <button
            onClick={() => setShowUpload(true)}
            className="flex items-center gap-1.5 px-4 py-2 text-sm font-medium bg-blue-600 text-white rounded-lg hover:bg-blue-700"
          >
            <Plus className="w-4 h-4" /> Upload Document
          </button>
        )}
        </div>
      </div>

      {projReportNote && (
        <div className="bg-green-50 border border-green-200 text-green-800 text-sm px-4 py-2.5 rounded-lg flex items-center justify-between">
          {projReportNote}
          <button onClick={() => setProjReportNote(null)} className="text-green-800 hover:text-green-900">&times;</button>
        </div>
      )}

      {error && (
        <div className="bg-red-50 border border-red-200 text-red-700 text-sm px-4 py-2.5 rounded-lg flex items-center justify-between">
          {error}
          <button onClick={() => setError(null)} className="text-red-700 hover:text-red-900"><X className="w-4 h-4" /></button>
        </div>
      )}

      {docs.length > 0 && (
        <div className="text-[11px] leading-relaxed text-gray-600 bg-blue-50/60 border border-blue-100 rounded-lg px-3 py-2 flex items-start gap-2">
          <span className="mt-px">💡</span>
          <span>
            <b>{tDoc('click_a_document')}</b> to open it. Inside, <b>select any text</b> to comment on that exact line ·
            {' '}<b>reply</b> to discuss · <b>click a comment</b> to jump to its highlighted spot · then
            {' '}<b>{tDoc('generate_report_ai')}</b> — built from the team's comments — or <b>{tDoc('submit_as_deliverable')}</b>.
            {' '}You can <b>edit or delete</b> your own comments anytime.
          </span>
        </div>
      )}

      {docs.length === 0 && (
        <div className="text-center py-12 bg-gray-50 rounded-lg border border-dashed border-gray-300">
          <FileText className="w-10 h-10 text-gray-300 mx-auto mb-2" />
          <p className="text-sm text-gray-500">No documents shared yet — upload the first one for your team.</p>
        </div>
      )}

      {docs.map(doc => {
        const isOpen = expanded === doc.id
        const myLevel = doc.my_permission
        const th = comments[doc.id]
        const docLocked = isArchived || !!doc.archived_at
        const canDownload = !isArchived || canManage
        return (
          <div
            key={doc.id}
            id={`doc-${doc.id}`}
            className={`bg-white border rounded-xl overflow-hidden transition-all ${String(doc.id) === highlighted ? 'ring-2 ring-blue-400 border-blue-300' : 'border-gray-200'}`}
          >
            {/* Card header */}
            <div className="px-4 py-3 flex items-start justify-between gap-3">
              <div className="flex-1 min-w-0 cursor-pointer group" onClick={() => openViewer(doc)} title="Open this document">
                <div className="flex items-center gap-2 flex-wrap">
                  <h4 className="text-sm font-semibold text-gray-800 truncate group-hover:text-blue-700 group-hover:underline">{doc.title}</h4>
                  <span className="text-[10px] font-medium border border-teal-200 bg-teal-50 text-teal-700 rounded-full px-2 py-0.5">v{doc.current_version}</span>
                  <span className={`text-[10px] font-medium border rounded-full px-2 py-0.5 ${LEVEL_BADGE[myLevel] || LEVEL_BADGE.view}`}>{myLevel}</span>
                  {doc.archived_at && (
                    <span className="text-[10px] font-medium border border-indigo-200 bg-indigo-50 text-indigo-700 rounded-full px-2 py-0.5 inline-flex items-center gap-0.5">
                      🔒 Archived {formatDate(doc.archived_at)}
                    </span>
                  )}
                  {doc.comment_count > 0 && (
                    <span className="text-[10px] text-gray-500 inline-flex items-center gap-0.5"><MessageSquare className="w-3 h-3" /> {doc.comment_count}</span>
                  )}
                </div>
                <p className="text-xs text-gray-500 mt-0.5 truncate">
                  {doc.latest?.original_filename}
                  {doc.latest?.file_size_bytes ? ` · ${(doc.latest.file_size_bytes / 1024 / 1024).toFixed(2)} MB` : ''}
                  {' · '}by {doc.uploader_name}
                  {doc.updated_at ? ` · ${formatDate(doc.updated_at)}` : ''}
                </p>
                {doc.description && <p className="text-xs text-gray-400 mt-0.5 truncate">{doc.description}</p>}
              </div>
              <div className="flex items-center gap-1.5 shrink-0">
                <button
                  onClick={() => openViewer(doc)}
                  className="flex items-center gap-1 px-2.5 py-1 text-xs text-blue-700 border border-blue-200 rounded hover:bg-blue-50"
                ><Eye className="w-3.5 h-3.5" /> Open</button>
                {canDownload && (
                  <button
                    onClick={() => download(doc)}
                    className="flex items-center gap-1 px-2.5 py-1 text-xs text-gray-700 border border-gray-200 rounded hover:bg-gray-50"
                  ><Download className="w-3.5 h-3.5" /></button>
                )}
                {myLevel === 'edit' && !docLocked && (
                  <button
                    onClick={() => setVerModal(doc)}
                    className="flex items-center gap-1 px-2.5 py-1 text-xs text-green-700 border border-green-200 bg-green-50 rounded hover:bg-green-100"
                  ><Upload className="w-3.5 h-3.5" /> New version</button>
                )}
                {canManage && !docLocked && (
                  <button
                    onClick={() => archiveDoc(doc)}
                    title="Archive this document (starts its 2-year retention clock)"
                    className="flex items-center gap-1 px-2.5 py-1 text-xs text-indigo-700 border border-indigo-200 bg-indigo-50 rounded hover:bg-indigo-100"
                  ><Archive className="w-3.5 h-3.5" /> Archive</button>
                )}
                {user?.role === 'Admin' && doc.archived_at && !isArchived && (
                  <button
                    onClick={() => unarchiveDoc(doc)}
                    title="Undo this archive (Admin): unlocks the document and clears its retention clock"
                    className="flex items-center gap-1 px-2.5 py-1 text-xs text-amber-700 border border-amber-200 bg-amber-50 rounded hover:bg-amber-100"
                  ><Archive className="w-3.5 h-3.5" /> Un-archive</button>
                )}
                {canDelete(doc) && !docLocked && (
                  <button onClick={() => removeDoc(doc)} className="p-1.5 text-gray-400 hover:text-red-500 hover:bg-red-50 rounded">
                    <Trash2 className="w-3.5 h-3.5" />
                  </button>
                )}
                <button onClick={() => toggleExpand(doc)} className="p-1.5 text-gray-400 hover:text-gray-700 rounded">
                  <ChevronDown className={`w-4 h-4 transition-transform ${isOpen ? 'rotate-180' : ''}`} />
                </button>
              </div>
            </div>

            {isOpen && (
              <div className="border-t border-gray-100 px-4 py-3 space-y-4 bg-gray-50/50">
                {/* ① AI Summary Sheet */}
                {(doc.ai_summary || doc.ai_comment_summary || (versions[doc.id] || []).some(v => v.ai_change_summary)) && (
                  <div className="bg-white border border-teal-100 rounded-lg p-3">
                    <p className="text-[10px] font-semibold text-teal-700 uppercase tracking-wider mb-1.5 inline-flex items-center gap-1">
                      <Sparkles className="w-3 h-3" /> Summary sheet (AI)
                    </p>
                    {doc.ai_summary && <p className="text-xs text-gray-700 leading-relaxed">{doc.ai_summary}</p>}
                    {(versions[doc.id] || []).filter(v => v.ai_change_summary).sort((a, b) => a.version - b.version).map(v => (
                      <p key={v.version} className="text-xs text-gray-600 leading-relaxed mt-2">
                        <span className="font-semibold text-teal-700">v{v.version}</span> — {v.ai_change_summary}
                      </p>
                    ))}
                    {doc.ai_comment_summary && (
                      <p className="text-xs text-gray-600 leading-relaxed mt-2 pt-2 border-t border-teal-50">
                        <span className="font-semibold text-teal-700">💬 Comments</span> — {doc.ai_comment_summary}
                      </p>
                    )}
                  </div>
                )}
                {/* Document Flow: review the summary, then hand a copy to the data assistant.
                    Only appears once an assistant is actually connected, so teams with nothing to
                    do with it never see the option. */}
                {assistantOn && !isArchived && (
                  <div className="bg-white border border-indigo-100 rounded-lg p-3">
                    <button
                      onClick={() => setFlowOpen(f => (f === doc.id ? null : doc.id))}
                      className="flex items-center gap-1.5 text-[10px] font-semibold text-indigo-700 uppercase tracking-wider"
                    >
                      <Sparkles className="w-3 h-3" /> Data assistant
                      <ChevronDown className={`w-3 h-3 transition-transform ${flowOpen === doc.id ? 'rotate-180' : ''}`} />
                    </button>
                    {flowOpen === doc.id && (
                      <div className="mt-3">
                        <DocumentFlowPanel projectId={project.id} documentId={doc.id} />
                      </div>
                    )}
                  </div>
                )}
                {/* AI summary of the comment thread — on demand (Mam #22) */}
                {doc.comment_count > 0 && !isArchived && (
                  <button
                    onClick={() => summarizeComments(doc)}
                    disabled={aiSumBusy === doc.id}
                    className="flex items-center gap-1 px-2.5 py-1 text-xs text-teal-700 border border-teal-200 bg-teal-50 rounded hover:bg-teal-100 disabled:opacity-50"
                  >
                    {aiSumBusy === doc.id
                      ? <><Loader2 className="w-3 h-3 animate-spin" /> Summarizing comments…</>
                      : <><Sparkles className="w-3 h-3" /> {doc.ai_comment_summary ? 'Refresh comment summary (AI)' : 'Summarize comments (AI)'}</>}
                  </button>
                )}
                {/* 4.3 + 4.4: generate an AI report from comments, and submit a document as a deliverable */}
                {!docLocked && (
                  <div className="flex flex-wrap gap-2 mt-1">
                    {myLevel !== 'view' && (
                      <button
                        onClick={() => generateReport(doc)}
                        disabled={reportBusy === doc.id}
                        className="flex items-center gap-1 px-2.5 py-1 text-xs text-indigo-700 border border-indigo-200 bg-indigo-50 rounded hover:bg-indigo-100 disabled:opacity-50"
                      >
                        {reportBusy === doc.id
                          ? <><Loader2 className="w-3 h-3 animate-spin" /> Generating report…</>
                          : <><Sparkles className="w-3 h-3" /> Generate report (AI)</>}
                      </button>
                    )}
                    {isConsultant && (
                      <button
                        onClick={() => openSubmitDeliv(doc)}
                        className="flex items-center gap-1 px-2.5 py-1 text-xs text-emerald-700 border border-emerald-200 bg-emerald-50 rounded hover:bg-emerald-100"
                      >
                        📎 Submit as deliverable
                      </button>
                    )}
                  </div>
                )}

                {/* ② Version history */}
                <div>
                  <p className="text-[10px] font-semibold text-gray-500 uppercase tracking-wider mb-1.5 inline-flex items-center gap-1">
                    <History className="w-3 h-3" /> Version history
                  </p>
                  <div className="space-y-1">
                    {(versions[doc.id] || []).map(v => (
                      <div key={v.version} className="flex items-center justify-between gap-2 text-xs bg-white border border-gray-200 rounded px-2.5 py-1.5">
                        <span className="min-w-0 truncate text-gray-700">
                          <span className="font-semibold">v{v.version}</span> · {v.original_filename} · {v.uploaded_by_name}
                          {v.note ? <span className="text-gray-400"> — "{v.note}"</span> : null}
                          {v.created_at ? <span className="text-gray-400"> · {formatDate(v.created_at)}</span> : null}
                        </span>
                        <span className="flex items-center gap-2 shrink-0">
                          <button
                            onClick={() => openViewer(doc, v.version)}
                            className="text-blue-600 hover:text-blue-800 inline-flex items-center gap-0.5"
                          ><Eye className="w-3 h-3" /> View</button>
                          {canDownload && (
                            <button onClick={() => download(doc, v.version)} className="text-gray-600 hover:text-gray-800"><Download className="w-3 h-3" /></button>
                          )}
                        </span>
                      </div>
                    ))}
                  </div>
                </div>

                {/* ③ Comments */}
                <div>
                  <p className="text-[10px] font-semibold text-gray-500 uppercase tracking-wider mb-1.5 inline-flex items-center gap-1">
                    <MessageSquare className="w-3 h-3" /> Comments
                  </p>
                  <div className="space-y-1.5">
                    {(() => {
                      const all = th?.comments || []
                      const roots = all.filter(c => !c.parent_id)
                      const repliesOf = (id) => all.filter(c => c.parent_id === id)
                      const renderComment = (c, isReply) => (
                        <div key={c.id} className={`text-xs bg-white border border-gray-200 rounded px-2.5 py-1.5 ${isReply ? 'ml-4 mt-1 border-l-2 border-l-blue-200' : ''}`}>
                          <span className="font-semibold text-gray-800">{c.user_name}</span>
                          <span className="text-gray-400"> · v{c.version_no}{c.created_at ? ` · ${formatDate(c.created_at)}` : ''}{c.edited_at ? ' · edited' : ''}</span>
                          {c.quoted_text && (
                            <blockquote className="mt-1 border-l-2 border-amber-300 bg-amber-50 text-gray-600 italic px-2 py-0.5 rounded-r">
                              "{c.quoted_text}"
                            </blockquote>
                          )}
                          {editingComment === c.id ? (
                            <div className="mt-1">
                              <textarea rows={2} value={editCommentText} onChange={(e) => setEditCommentText(e.target.value)}
                                className="w-full text-xs border border-gray-300 rounded-lg px-2 py-1 focus:outline-none focus:ring-2 focus:ring-blue-500/20" />
                              <div className="flex gap-2 mt-1">
                                <button onClick={() => saveEditComment(doc, c)} disabled={!editCommentText.trim()}
                                  className="px-2 py-0.5 text-[10px] bg-blue-600 text-white rounded hover:bg-blue-700 disabled:opacity-50">{tc('actions.save')}</button>
                                <button onClick={cancelEditComment} className="px-2 py-0.5 text-[10px] text-gray-500 hover:text-gray-700">{tc('actions.cancel')}</button>
                              </div>
                            </div>
                          ) : (
                            <p className="text-gray-700 mt-0.5 whitespace-pre-wrap">{c.content}</p>
                          )}
                          {editingComment !== c.id && (
                            <div className="flex items-center gap-2 mt-0.5">
                              {!docLocked && myLevel !== 'view' && !isReply && (
                                <button onClick={() => { setReplyTo({ id: c.id, user_name: c.user_name }); setExpanded(doc.id) }}
                                  className="text-[10px] text-blue-600 hover:underline">{tDoc('reply')}</button>
                              )}
                              {!docLocked && canEditComment(c) && (
                                <>
                                  <button onClick={() => startEditComment(c)} className="text-[10px] text-gray-500 hover:underline">{tc('actions.edit')}</button>
                                  <button onClick={() => deleteComment(doc, c)} className="text-[10px] text-red-500 hover:underline">{tc('actions.delete')}</button>
                                </>
                              )}
                            </div>
                          )}
                          {repliesOf(c.id).map(r => renderComment(r, true))}
                        </div>
                      )
                      return roots.length ? roots.map(c => renderComment(c, false))
                        : <p className="text-xs text-gray-400">{tDoc('no_comments')}</p>
                    })()}
                  </div>
                  {docLocked ? (
                    <p className="text-[11px] text-gray-400 mt-1.5">{tDoc('archived_readonly')}</p>
                  ) : myLevel !== 'view' ? (
                    <div className="mt-2">
                      {expanded === doc.id && replyTo && (
                        <div className="flex items-center justify-between text-[11px] bg-blue-50 border border-blue-200 rounded px-2 py-1 mb-1">
                          <span className="text-blue-700">Replying to {replyTo.user_name}</span>
                          <button onClick={() => setReplyTo(null)} className="text-blue-500 hover:text-blue-700">✕</button>
                        </div>
                      )}
                      {expanded === doc.id && quotedText && (
                        <div className="flex items-start justify-between gap-2 text-[11px] bg-amber-50 border border-amber-200 rounded px-2 py-1 mb-1">
                          <span className="text-gray-600 italic truncate">💬 On: "{quotedText}"</span>
                          <button onClick={() => setQuotedText('')} className="text-amber-600 hover:text-amber-800 shrink-0">✕</button>
                        </div>
                      )}
                      <div className="flex gap-2">
                        <textarea
                          rows={1}
                          value={expanded === doc.id ? newComment : ''}
                          onChange={(e) => setNewComment(e.target.value)}
                          placeholder={replyTo ? 'Write a reply…' : 'Write a comment…'}
                          className="flex-1 text-xs border border-gray-300 rounded-lg px-2.5 py-1.5 focus:outline-none focus:ring-2 focus:ring-blue-500/20"
                        />
                        <button
                          onClick={() => { setExpanded(doc.id); captureSelection() }}
                          title="Select text in the document (or anywhere on the page), then click this to attach it to your comment"
                          className="px-2 py-1.5 text-xs border border-amber-300 bg-amber-50 text-amber-700 rounded-lg hover:bg-amber-100"
                        >❝ Selection</button>
                        <button
                          onClick={() => postComment(doc)}
                          disabled={commentBusy || !newComment.trim()}
                          className="px-3 py-1.5 text-xs bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50"
                        >{commentBusy ? '…' : (replyTo ? 'Reply' : 'Post')}</button>
                      </div>
                    </div>
                  ) : (
                    <p className="text-[11px] text-gray-400 mt-1.5">{tDoc('view_only_no_comment')}</p>
                  )}
                </div>

                {/* ④ Permissions (PM/Admin only) */}
                {canManage && (
                  <div>
                    <p className="text-[10px] font-semibold text-gray-500 uppercase tracking-wider mb-1.5 inline-flex items-center gap-1">
                      <Shield className="w-3 h-3" /> Permissions (per member)
                    </p>
                    <div className="space-y-1">
                      {(perms[doc.id] || []).map(m => (
                        <div key={m.user_id} className="flex items-center justify-between gap-2 text-xs bg-white border border-gray-200 rounded px-2.5 py-1.5">
                          <span className="min-w-0 truncate text-gray-700">
                            {m.name} <span className="text-gray-400">· {m.role}{m.is_uploader ? ' · uploader' : ''}</span>
                          </span>
                          {m.locked ? (
                            <span className="text-[10px] text-gray-400 shrink-0">always edit (PM/Admin)</span>
                          ) : (
                            <select
                              value={m.level}
                              onChange={(e) => setLevel(doc, m.user_id, e.target.value)}
                              disabled={docLocked}
                              className="text-xs border border-gray-300 rounded px-1.5 py-0.5 bg-white disabled:opacity-50 disabled:cursor-not-allowed"
                            >
                              {LEVEL_OPTIONS.map(l => <option key={l} value={l}>{l}</option>)}
                            </select>
                          )}
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>
        )
      })}

      {/* Upload modal */}
      {showUpload && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4">
          <form onSubmit={submitUpload} className="bg-white rounded-xl shadow-2xl max-w-md w-full p-6 space-y-4" onClick={e => e.stopPropagation()}>
            <h2 className="text-lg font-bold text-gray-900">{tDoc('upload_documents')}</h2>
            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1">
                Title {upFiles.length > 1 ? '' : '*'}
              </label>
              <input type="text" value={upTitle} onChange={(e) => setUpTitle(e.target.value)}
                required={upFiles.length <= 1} disabled={upFiles.length > 1}
                className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm disabled:bg-gray-50 disabled:text-gray-400"
                placeholder={upFiles.length > 1 ? 'Each file keeps its own name' : 'e.g. Workshop Concept Note'} />
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1">{tDoc('description_optional')}</label>
              <textarea rows={2} value={upDesc} onChange={(e) => setUpDesc(e.target.value)}
                className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm" placeholder="What is this document for?" />
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1">{tDoc('files_req')}</label>
              <input type="file" multiple required onChange={(e) => setUpFiles([...(e.target.files || [])])} className="text-sm" />
              <p className="text-[10px] text-gray-400 mt-1">pdf, docx, xlsx, pptx, images, csv, zip… (max 50 MB each — pick as many as you like). Team members are notified.</p>
              {upFiles.length > 1 && (
                <p className="text-[11px] text-gray-500 mt-1">{upFiles.map(f => f.name).join(' · ')}</p>
              )}
            </div>
            <div className="flex justify-end gap-2">
              <button type="button" onClick={() => setShowUpload(false)} disabled={uploading} className="px-3 py-2 text-sm text-gray-600 hover:bg-gray-100 rounded-lg">{tc('actions.cancel')}</button>
              <button type="submit"
                disabled={uploading || !upFiles.length || (upFiles.length === 1 && !upTitle.trim())}
                className="px-4 py-2 text-sm bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 inline-flex items-center gap-1.5">
                {uploading
                  ? <><Loader2 className="w-4 h-4 animate-spin" /> Uploading {upProgress ? `${upProgress.done + 1} of ${upProgress.total}` : ''}…</>
                  : <>{upFiles.length > 1 ? `Upload ${upFiles.length} files` : 'Upload'}</>}
              </button>
            </div>
          </form>
        </div>
      )}

      {/* New-version modal */}
      {verModal && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4">
          <form onSubmit={submitVersion} className="bg-white rounded-xl shadow-2xl max-w-md w-full p-6 space-y-4" onClick={e => e.stopPropagation()}>
            <h2 className="text-lg font-bold text-gray-900">Upload new version — {verModal.title}</h2>
            <p className="text-xs text-gray-500">This becomes v{(verModal.current_version || 1) + 1}. Previous versions stay in the history.</p>
            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1">{tDoc('edited_file_req')}</label>
              <input type="file" required onChange={(e) => setVerFile(e.target.files?.[0] || null)} className="text-sm" />
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1">{tDoc('what_changed')}</label>
              <textarea rows={2} value={verNote} onChange={(e) => setVerNote(e.target.value)}
                className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm" placeholder="e.g. Updated the budget table and added section 4" />
            </div>
            <div className="flex justify-end gap-2">
              <button type="button" onClick={() => setVerModal(null)} disabled={verBusy} className="px-3 py-2 text-sm text-gray-600 hover:bg-gray-100 rounded-lg">{tc('actions.cancel')}</button>
              <button type="submit" disabled={verBusy || !verFile} className="px-4 py-2 text-sm bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-50 inline-flex items-center gap-1.5">
                {verBusy ? <><Loader2 className="w-4 h-4 animate-spin" /> Uploading…</> : <>{tDoc('upload_version')}</>}
              </button>
            </div>
          </form>
        </div>
      )}

      {/* In-app file viewer — with the comment thread beside the document (Mam #22),
          so the team can read the sheet and comment in the same window. */}
      {viewer && (() => {
        const vDoc = viewer.doc ? (docs.find(d => d.id === viewer.doc.id) || viewer.doc) : null
        const all = vDoc ? (comments[vDoc.id]?.comments || []) : []
        const roots = all.filter(c => !c.parent_id)
        const repliesOf = (id) => all.filter(c => c.parent_id === id)
        const canComment = vDoc && !isArchived && !vDoc.archived_at && vDoc.my_permission !== 'view'

        // Per-author colours (MS-Word style): my own comments always yellow; each other
        // author gets a distinct colour, listed in the legend. Only anchored comments
        // (those with a quoted passage) are highlighted in the document.
        const { colorOf, palette, highlights: vHighlights, legend } = commentColors(all, user?.id)
        const activeComment = activeAnchor != null ? all.find(c => c.id === activeAnchor) : null
        const activeColor = activeComment ? colorOf(activeComment).hlStrong : undefined
        const initials = initialsOf

        const renderComment = (c, isReply) => {
          const co = colorOf(c)
          return (
          <div key={c.id}
            onClick={c.quoted_text ? (e) => { e.stopPropagation(); setActiveAnchor(c.id) } : undefined}
            title={c.quoted_text ? 'Click to find & highlight this passage in the document' : undefined}
            className={`text-xs bg-gray-50 border border-gray-200 rounded px-2.5 py-1.5 ${isReply ? 'ml-3 mt-1 border-l-2 border-l-blue-200' : ''} ${c.quoted_text ? 'cursor-pointer hover:border-amber-300' : ''} ${activeAnchor === c.id ? 'ring-2 ring-amber-400' : ''}`}>
            {c.quoted_text && (
              <span className="inline-flex items-center justify-center w-4 h-4 rounded text-[8px] font-bold text-white align-middle mr-1" style={{ backgroundColor: co.sw }}>{initials(c.user_name)}</span>
            )}
            <span className="font-semibold text-gray-800">{c.user_name}</span>
            <span className="text-gray-400"> · v{c.version_no}{c.created_at ? ` · ${formatDate(c.created_at)}` : ''}{c.edited_at ? ' · edited' : ''}</span>
            {c.quoted_text && (
              <blockquote className="mt-1 border-l-2 text-gray-600 italic px-2 py-0.5 rounded-r" style={{ borderColor: co.sw, backgroundColor: co.hl }}>
                "{c.quoted_text}"
              </blockquote>
            )}
            {editingComment === c.id ? (
              <div className="mt-1" onClick={(e) => e.stopPropagation()}>
                <textarea rows={2} value={editCommentText} onChange={(e) => setEditCommentText(e.target.value)}
                  className="w-full text-xs border border-gray-300 rounded-lg px-2 py-1 focus:outline-none focus:ring-2 focus:ring-blue-500/20" />
                <div className="flex gap-2 mt-1">
                  <button onClick={() => saveEditComment(vDoc, c)} disabled={!editCommentText.trim()}
                    className="px-2 py-0.5 text-[10px] bg-blue-600 text-white rounded hover:bg-blue-700 disabled:opacity-50">{tc('actions.save')}</button>
                  <button onClick={cancelEditComment} className="px-2 py-0.5 text-[10px] text-gray-500 hover:text-gray-700">{tc('actions.cancel')}</button>
                </div>
              </div>
            ) : (
              <p className="text-gray-700 mt-0.5 whitespace-pre-wrap">{c.content}</p>
            )}
            {editingComment !== c.id && (
              <div className="flex items-center gap-2 mt-0.5">
                {canComment && !isReply && (
                  <button onClick={(e) => { e.stopPropagation(); setReplyTo({ id: c.id, user_name: c.user_name }) }}
                    className="text-[10px] text-blue-600 hover:underline">{tDoc('reply')}</button>
                )}
                {!isArchived && !vDoc.archived_at && canEditComment(c) && (
                  <>
                    <button onClick={(e) => { e.stopPropagation(); startEditComment(c) }} className="text-[10px] text-gray-500 hover:underline">{tc('actions.edit')}</button>
                    <button onClick={(e) => { e.stopPropagation(); deleteComment(vDoc, c) }} className="text-[10px] text-red-500 hover:underline">{tc('actions.delete')}</button>
                  </>
                )}
              </div>
            )}
            {repliesOf(c.id).map(r => renderComment(r, true))}
          </div>
          )
        }
        return (
        <FileViewerModal
          open={!!viewer}
          onClose={() => { setViewer(null); setViewerComment(''); setQuotedText(''); setReplyTo(null); setActiveAnchor(null); cancelEditComment() }}
          fetchUrl={viewer.url}
          filename={viewer.filename}
          onDownload={viewer.download}
          onTextSelect={captureSelection}
          highlights={vHighlights}
          palette={palette}
          activeHighlightId={activeAnchor}
          activeColor={activeColor}
          sidePanel={vDoc ? (
              <>
                <div className="px-3 py-2.5 border-b border-gray-200 shrink-0">
                  <p className="text-xs font-semibold text-gray-700 inline-flex items-center gap-1">
                    <MessageSquare className="w-3.5 h-3.5" /> Comments ({all.length})
                  </p>
                  <p className="text-[10px] text-gray-400 mt-0.5">Select text to comment on it · click a comment to jump to its highlight.</p>
                  {legend.length > 0 && (
                    <div className="flex flex-wrap gap-x-2 gap-y-0.5 mt-1.5">
                      {legend.map((l, i) => (
                        <span key={i} className="inline-flex items-center gap-1 text-[10px] text-gray-600">
                          <span className="w-2.5 h-2.5 rounded-sm inline-block" style={{ backgroundColor: l.sw }} />{l.label}
                        </span>
                      ))}
                    </div>
                  )}
                </div>
                <div className="flex-1 overflow-y-auto px-3 py-2 space-y-1.5">
                  {roots.length ? roots.map(c => renderComment(c, false))
                    : <p className="text-xs text-gray-400 pt-2 text-center">{tDoc('no_comments_start')}</p>}
                </div>
                {canComment ? (
                  <div className="p-2.5 border-t border-gray-200 shrink-0">
                    {replyTo && (
                      <div className="flex items-center justify-between text-[11px] bg-blue-50 border border-blue-200 rounded px-2 py-1 mb-1">
                        <span className="text-blue-700 truncate">Replying to {replyTo.user_name}</span>
                        <button onClick={() => setReplyTo(null)} className="text-blue-500 hover:text-blue-700 shrink-0">✕</button>
                      </div>
                    )}
                    {quotedText && (
                      <div className="flex items-start justify-between gap-2 text-[11px] bg-amber-50 border border-amber-200 rounded px-2 py-1 mb-1">
                        <span className="text-gray-600 italic truncate">💬 On: "{quotedText}"</span>
                        <button onClick={() => setQuotedText('')} className="text-amber-600 hover:text-amber-800 shrink-0">✕</button>
                      </div>
                    )}
                    <textarea
                      rows={2}
                      value={viewerComment}
                      onChange={(e) => setViewerComment(e.target.value)}
                      placeholder={replyTo ? 'Write a reply…' : (quotedText ? 'Comment on the selected text…' : 'Comment on this document…')}
                      className="w-full text-xs border border-gray-300 rounded-lg px-2.5 py-1.5 focus:outline-none focus:ring-2 focus:ring-blue-500/20 resize-none"
                    />
                    <div className="flex gap-1.5 mt-1.5">
                      <button
                        onMouseDown={(e) => { e.preventDefault(); captureSelection() }}
                        title="Select text in the document, then click to attach that passage to your comment"
                        className="px-2 py-1.5 text-xs border border-amber-300 bg-amber-50 text-amber-700 rounded-lg hover:bg-amber-100 shrink-0"
                      >❝ Selection</button>
                      <button
                        onClick={postViewerComment}
                        disabled={viewerCommentBusy || !viewerComment.trim()}
                        className="flex-1 px-3 py-1.5 text-xs bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50"
                      >{viewerCommentBusy ? 'Posting…' : (replyTo ? 'Reply' : 'Post comment')}</button>
                    </div>
                  </div>
                ) : (
                  <p className="p-2.5 border-t border-gray-200 text-[11px] text-gray-400 text-center shrink-0">
                    {isArchived || vDoc.archived_at ? 'This document is archived — comments are read-only.' : 'You have view-only access — commenting is disabled.'}
                  </p>
                )}
              </>
          ) : undefined}
        />
        )
      })()}

      {/* 4.4: pick a milestone to submit this document against */}
      {delivFor && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4" onClick={() => setDelivFor(null)}>
          <div className="bg-white rounded-xl shadow-2xl max-w-md w-full" onClick={e => e.stopPropagation()}>
            <div className="px-5 py-4 border-b border-gray-200">
              <h2 className="text-lg font-bold text-gray-900">{tDoc('submit_as_deliverable')}</h2>
              <p className="text-xs text-gray-500 mt-0.5">"{delivFor.title}" will be submitted as your deliverable for the chosen milestone.</p>
            </div>
            <div className="px-5 py-4 space-y-3">
              {delivMs.length === 0 ? (
                <p className="text-sm text-gray-500">You have no milestones on this project that can take a deliverable yet.</p>
              ) : (
                <div>
                  <label className="block text-xs font-medium text-gray-600 mb-1">{tDoc('milestone_label')}</label>
                  <select value={delivPick} onChange={e => setDelivPick(e.target.value)}
                    className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm bg-white">
                    <option value="">— select —</option>
                    {delivMs.map(m => <option key={m.id} value={m.id}>{m.title}</option>)}
                  </select>
                </div>
              )}
            </div>
            <div className="flex justify-end gap-2 px-5 py-3 border-t border-gray-200 bg-gray-50">
              <button onClick={() => setDelivFor(null)} className="px-4 py-2 text-sm border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50">{tc('actions.cancel')}</button>
              <button onClick={doSubmitDeliv} disabled={delivBusy || !delivPick}
                className="px-5 py-2 text-sm bg-emerald-600 text-white font-medium rounded-lg hover:bg-emerald-700 disabled:opacity-50">
                {delivBusy ? 'Submitting…' : 'Submit'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
